import Model from '@/store/plugins/orm/Model'
import { Role, UserTransaction, Session, Shift } from './ModelRepository'

import HashGen from '@/utilities/HashGen';

export class User extends Model {
    static get entity() {
        return 'users';
    }

    static get STATUS_ACTIVE() {
        return 'active';
    }
    static get STATUS_DISABLED() {
        return 'disabled';
    }

    static fields() {
        return {
            id: this.attr(null),
            hash: this.attr(null),
            username: this.attr(null),
            name: this.attr(null),
            pin: this.attr(null),
            role_id: this.attr(null),
            status: this.attr(null),
            receipts_allowed: this.attr(true, value => !!value),
            charged: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            deposit: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            cash: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),

            role: this.belongsTo(Role, 'role_id'),

            sync_needed: this.attr(null)
        };
    }

    $duplicate() {
        return new User(this.$toJson());
    }

    /**
     * Add User's deposit
     */
    async addDeposit(deposit) {
        let depositToAdd = Money.parse(deposit).toFormat('0.0000');
        return this.addTransaction(depositToAdd, UserTransaction.TYPE_DEPOSIT);
    }

    /**
     * Add User's charge
     */
    async addCharge(charge) {
        let chargeToAdd = Money.parse(charge).toFormat('0.0000');
        return this.addTransaction(chargeToAdd, UserTransaction.TYPE_CHARGE);
    }

    /**
     * Add User Transaction
     */
    async addTransaction(value, type) {
        if(!this.$store().getters.tenant) {
            return { success: false };
        }

        let curShift = Shift.current();
        if(!curShift) return { success: false };

        let transaction = new UserTransaction({
            hash: HashGen.regular(),
            user_id: this.id,
            shift_id: curShift.id,
            shift_hash: curShift.hash,
            amount: value,
            type: type,
            issued_at: moment().utc().format(UserTransaction.DATE_FORMAT),
            sync_needed: this.$store().getters.tenant.venue.hash,
        })

        return transaction.sync();
    }

    /**
     * Clear user's debt
     */
    async clearDebt() {
        let chargeToAdd = this.getDebt().toUnit();
        return this.addCharge(chargeToAdd)
    }

    /**
     * Open a new session. Called only when offline. When online
     * session is created on the server and stored to client.
     */
    clockIn() {
        Session.open(this);
    }

    /**
     * Close the existing session. Called only when offline. When online
     * session is closed on the server and updated session is stored to client.
     */
    clockOut() {
        this.getSession()
            .$duplicate()
            .close();
    }

    /**
     * Set / Update user session with the data returned from server
     */
    setSession(session) {
        let activeSession = this.getSession();

        if (!activeSession) {
            Session.insert({ data: [session] });
            return;
        }

        activeSession = activeSession.$duplicate();
        activeSession.id = session.id;
        activeSession.clock_in = session.clock_in;
        activeSession.clock_out = session.clock_out;
        activeSession.ip_address = session.ip_address;
        activeSession.sync_needed = null;
        activeSession.$save();
    }

    /**
     * Lazy get session
     */
    getSession() {
        return Session.query()
            .where(session => {
                return session.user_id === this.id && !!session.clock_in && !session.clock_out;
            })
            .first();
    }

    getCharged() {
        const shift = Shift.current();
        const transactions = shift ? shift.getUserTransactions() : [];

        return transactions.filter(t => t.user_id == this.id)
            .filter(t => t.type === UserTransaction.TYPE_CHARGE)
            .reduce((acc, cur) => acc.add(cur.getAmount()), Money.parse(0));
    }

    getDeposit() {
        const shift = Shift.current();
        const transactions = shift ? shift.getUserTransactions() : [];

        return transactions.filter(t => t.user_id == this.id)
            .filter(t => t.type === UserTransaction.TYPE_DEPOSIT)
            .reduce((acc, cur) => acc.add(cur.getAmount()), Money.parse(0));
    }

    getCash() {
        const shift = Shift.current();
        const orders = shift ? shift.getOrders() : [];

        const userOrders = orders.filter(order => {
            const userId = Number(order.getFlag('clearDebtForUser')[0]) || order.user_id
            return userId == this.id
        })

        return userOrders.filter(order => order.isCashExchanged())
            .reduce((acc, cur) => acc.add(cur.getPaidCash()), Money.parse(0));
    }

    getDebt() {
        return this.getDeposit().add(this.getCash()).subtract(this.getCharged());
    }

    getTables() {
        const tablePermissions = this.$store().getters.tablePermissions;

        const tables = Table.all();
        const unpaidOrders = Order.onTables();

        return tables.filter(table => {
            const tableOrders = unpaidOrders.filter(o => o.table_id === table.id);
            if(!tableOrders.length) return false;

            if(tablePermissions[table.id]) {
                return tablePermissions[table.id] === this.id;
            }

            return tableOrders.some(order => order.user_id === this.id)
        })
    }
}

export default User
